import React, { useMemo, useCallback, useEffect, useRef, useState } from 'react';
import { ActivityIndicator, StyleSheet, View, Pressable, Platform } from 'react-native';
import PropTypes from 'prop-types';
import { useTheme } from '@/context/ThemeContext';
import { isDarkTheme } from '@/constants/Colors';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import { useModalStore, useAuthStore, useCreditPacksStore, useToastStore } from '@/store';
import { useGabPurchase } from '@/context/GabPurchaseContext';
import { getCreditPackIapProductId } from '@/hooks/useIosIap';
import AppStoreCatalogStatus from '@/components/Credits/AppStoreCatalogStatus';
import { usesStoreBilling } from '@/lib/appDistribution';
import { formatCreditBalance, wholeCredits } from '@/lib/formatCredits';
import { getPlusCreditOptions, formatCreditResetDate, startAryaChat } from '@/lib/plusCreditOptions';
import copyText from '@/constants/utils/copy_text';
import useTranslation from '@/hooks/useTranslation';
import { trackConversionEvent, setCheckoutSourceDimension, CONVERSION_ACTION, eventName } from '@/constants/utils/analyticsEvents';

const DEPLETED_SURFACE = 'credits_depleted';

// Shared React Native layout: mobile, both Mac editions, and React Native Web.
const createStyles = (dark) => {
  const ink = dark ? '#F5F5F5' : '#202124';
  const muted = dark ? '#ABABAE' : '#68686D';
  const line = dark ? '#414144' : '#E7E7EA';
  return StyleSheet.create({
    card: { width: '100%', maxWidth: 520, marginBottom: 8, padding: 20, gap: 20, borderRadius: 22, borderWidth: 1, borderColor: line, backgroundColor: dark ? '#29292B' : '#FFFFFF' },
    heading: { gap: 7 },
    title: { color: ink, letterSpacing: -0.4 },
    body: { color: muted, flexShrink: 1, lineHeight: 18 },
    ink: { color: ink },
    preview: { flexDirection: 'row', alignItems: 'center', gap: 8, borderLeftWidth: 2, borderLeftColor: line, paddingLeft: 12 },
    previewText: { flex: 1, minWidth: 0, gap: 3 },
    iconButton: { width: 44, minHeight: 44, alignItems: 'center', justifyContent: 'center', borderRadius: 10 },
    section: { gap: 12 },
    plusOffer: { gap: 16, paddingTop: 20, borderTopWidth: 1, borderTopColor: line },
    upgradeActions: { gap: 4 },
    creditAlternative: { minHeight: 44, alignItems: 'center', justifyContent: 'center', borderRadius: 10, paddingHorizontal: 10, paddingVertical: 10 },
    packs: { flexDirection: 'row', flexWrap: 'wrap', gap: 8 },
    pack: { flexGrow: 1, flexBasis: 80, minWidth: 76, alignItems: 'center', justifyContent: 'center', paddingHorizontal: 6, paddingVertical: 14, gap: 3, borderRadius: 12, borderWidth: 1, borderColor: line, backgroundColor: dark ? '#242426' : '#FAFAFB' },
    selectedPack: { borderColor: ink, backgroundColor: dark ? '#373739' : '#F0F0F2' },
    packPrice: { marginTop: 7, color: ink },
    primary: { minHeight: 48, borderRadius: 12, paddingHorizontal: 14, paddingVertical: 13, flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 8, backgroundColor: ink },
    primaryText: { color: dark ? '#202124' : '#FFFFFF', flexShrink: 1, textAlign: 'center' },
    secondary: { flexDirection: 'row', alignItems: 'center', gap: 12, paddingTop: 17, borderTopWidth: 1, borderTopColor: line, minHeight: 60 },
    footer: { flexDirection: 'row', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', columnGap: 12, marginTop: -10, marginBottom: -8 },
    link: { minHeight: 44, justifyContent: 'center', borderRadius: 6 },
    hover: { opacity: 0.7 },
    disabled: { opacity: 0.45 },
    status: { flexDirection: 'row', alignItems: 'center', gap: 10, minHeight: 44 },
  });
};

function CreditsDepletedMessage({ message }) {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const dark = isDarkTheme(theme);
  const styles = useMemo(() => createStyles(dark), [dark]);
  const { openModal } = useModalStore(state => state.actions);
  const { addToast } = useToastStore(state => state.actions);
  const { openCreditPacksCheckout, usesWhopCheckout, iap } = useGabPurchase();
  const user = useAuthStore(state => state.user);
  const creditPacks = useCreditPacksStore(state => state.creditPacks);
  const fetchCreditPacks = useCreditPacksStore(state => state.fetchCreditPacks);
  const isFetching = useCreditPacksStore(state => state.isLoading);
  const fetchError = useCreditPacksStore(state => state.error);
  const [selectedId, setSelectedId] = useState(null);
  const [isBuying, setIsBuying] = useState(false);
  const [purchaseError, setPurchaseError] = useState('');
  const buyingRef = useRef(false);
  const packRefs = useRef([]);
  const storeBilling = usesStoreBilling();
  const { isPlus, canUpgradeToPremium, monthlyTotal, resetDate, totalAvailable } = getPlusCreditOptions(user);

  useEffect(() => {
    trackConversionEvent(CONVERSION_ACTION.PAYWALL_SHOWN, eventName(DEPLETED_SURFACE, isPlus ? 'plus' : 'free'), totalAvailable);
    setCheckoutSourceDimension(DEPLETED_SURFACE);
    // Record the impression once, independently of subsequent balance changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isPlus) Promise.resolve(fetchCreditPacks()).catch(() => {});
  }, [fetchCreditPacks, isPlus]);

  const displayPacks = useMemo(() => (creditPacks || [])
    .filter(pack => pack.flags?.isDisplayed !== false && !pack.deletedAt && (!storeBilling || getCreditPackIapProductId(pack.credits)))
    .sort((a, b) => a.credits - b.credits)
    .slice(0, 3), [creditPacks, storeBilling]);
  // Start with the smallest offer; preserve the choice through catalog refreshes.
  const selectedPack = displayPacks.find(pack => pack.id === selectedId) || displayPacks[0];
  const priceFor = useCallback(pack => {
    if (!pack) return null;
    if (storeBilling) {
      const id = getCreditPackIapProductId(pack.credits);
      const product = iap?.getProduct?.(id);
      return iap?.getFormattedPrice?.(id) || product?.localizedPrice || product?.oneTimePurchaseOfferDetails?.formattedPrice || null;
    }
    if (!Number.isFinite(Number(pack.price)) || pack.price == null) return null;
    return Number(pack.price).toLocaleString('en-US', { style: 'currency', currency: pack.currency || 'USD' });
  }, [iap, storeBilling]);
  const selectedPrice = priceFor(selectedPack);
  const canBuy = !!selectedPack && !!selectedPrice && !isFetching && !fetchError && !isBuying && !iap?.purchaseInProgress;
  const originalMessage = useMemo(() => (message?.content || '').replace(/<[^>]*>?/gm, '').trim(), [message?.content]);

  const handleBuyCredits = () => {
    trackConversionEvent(CONVERSION_ACTION.CREDITS_DEPLETED_CLICKED, eventName(DEPLETED_SURFACE, 'buy_credits'));
    openModal('CreditsPurchaseModal', { initialTab: 'buy-credits' });
  };
  const handleUpgrade = () => {
    trackConversionEvent(CONVERSION_ACTION.CREDITS_DEPLETED_CLICKED, eventName(DEPLETED_SURFACE, 'upgrade'));
    openModal('PricingModal', isPlus ? { initialCadence: 'monthly' } : {
      title: t('messages.creditsDepleted.upgradeTitle'), subtitle: t('messages.creditsDepleted.upgradeSubtitle'),
    });
  };
  const handleBuy = async () => {
    if (!canBuy || buyingRef.current) return;
    buyingRef.current = true;
    setIsBuying(true);
    setPurchaseError('');
    trackConversionEvent(CONVERSION_ACTION.CREDITS_DEPLETED_CLICKED, eventName(DEPLETED_SURFACE, 'pack'), selectedPack.credits);
    try {
      const quickPurchase = !storeBilling && !usesWhopCheckout && user?.valmar?.hasPaymentMethod && user?.valmar?.maskedCardData?.maskedNumber;
      if (quickPurchase) openModal('QuickPurchaseConfirmModal', { creditPackId: selectedPack.id });
      else await openCreditPacksCheckout({ creditPackId: selectedPack.id, credits: selectedPack.credits });
    } catch (error) {
      if (!/checkout (?:closed|cancelled|canceled)|user closed|backdrop close|user.cancel/i.test(error?.message || error?.code || '')) {
        setPurchaseError(t('messages.creditsDepleted.purchaseErrorMsg'));
      }
    } finally {
      buyingRef.current = false;
      setIsBuying(false);
    }
  };
  const selectPack = id => { setSelectedId(id); setPurchaseError(''); };
  const onPackKeyDown = (event, index) => {
    if (isBuying) return;
    const key = event.nativeEvent?.key || event.key;
    const offsets = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 };
    if (!(key in offsets) && key !== 'Home' && key !== 'End') return;
    event.preventDefault();
    const next = key === 'Home' ? 0 : key === 'End' ? displayPacks.length - 1 : (index + offsets[key] + displayPacks.length) % displayPacks.length;
    selectPack(displayPacks[next].id);
    packRefs.current[next]?.focus();
  };
  const handleCopyMessage = async () => {
    try { await copyText(originalMessage); addToast(t('messages.copied'), 'success'); }
    catch (_) { addToast(t('messages.copyFailed'), 'error'); }
  };
  const quietPress = ({ hovered, pressed }) => [(hovered || pressed) && styles.hover];

  return (
    <View style={styles.card} testID="credits-depleted-card">
      <View style={styles.heading}>
        <Text size="xl" weight="bold" accessibilityRole="header" style={styles.title}>{t(totalAvailable > 0
          ? 'messages.creditsDepleted.notEnoughCredits'
          : 'messages.creditsDepleted.outOfCredits')}</Text>
        <Text size="n" style={styles.body}>{totalAvailable > 0
          ? t('messages.creditsDepleted.needMoreCredits', { count: formatCreditBalance(totalAvailable), s: wholeCredits(totalAvailable) === 1 ? '' : 's' })
          : t('messages.creditsDepleted.emptyBalanceHint')}</Text>
      </View>

      {!!originalMessage && <View style={styles.preview}>
        <View style={styles.previewText}>
          <Text size="xs" style={styles.body}>{t('messages.creditsDepleted.yourMessage')}</Text>
          <Text size="n" numberOfLines={2} style={styles.ink}>{originalMessage}</Text>
        </View>
        <Pressable onPress={handleCopyMessage} accessibilityRole="button" accessibilityLabel={t('messages.creditsDepleted.copyMessage')}
          style={state => [styles.iconButton, quietPress(state)]}>
          <Icon name="copy" size={15} color={dark ? '#ABABAE' : '#68686D'} />
        </Pressable>
      </View>}

      {!isPlus && <View style={styles.plusOffer}>
        <View style={styles.heading}>
          <Text size="n" weight="bold" style={styles.ink}>{t('messages.creditsDepleted.plusRecoveryTitle')}</Text>
          <Text size="n" style={styles.body}>{t('messages.creditsDepleted.plusRecoverySubtitle')}</Text>
        </View>
        <View style={styles.upgradeActions}>
          <Pressable onPress={handleUpgrade} accessibilityRole="button" style={state => [styles.primary, quietPress(state)]}>
            <Text size="n" weight="bold" style={styles.primaryText}>{t('messages.creditsDepleted.upgradePlus')}</Text>
          </Pressable>
          <Pressable onPress={handleBuyCredits} accessibilityRole="button" style={state => [styles.creditAlternative, quietPress(state)]}>
            <Text size="s" style={styles.body}>{t('messages.creditsDepleted.buyCreditsInstead')}</Text>
          </Pressable>
        </View>
      </View>}

      {isPlus && <View style={styles.section}>
        <Text size="xs" weight="medium" style={styles.body}>{t('messages.creditsDepleted.oneTimeTopUp')}</Text>
        <AppStoreCatalogStatus iap={iap} credits />
        {isFetching ? <View style={styles.status}><ActivityIndicator color={dark ? '#F5F5F5' : '#202124'} /><Text size="s" style={styles.body}>{t('messages.creditsDepleted.loadingPacks')}</Text></View>
          : fetchError || !displayPacks.length ? <Text size="s" style={styles.body} accessibilityLiveRegion="polite">{t('messages.creditsDepleted.packsUnavailable')}</Text>
            : <>
              <View style={styles.packs} accessibilityRole="radiogroup" accessibilityLabel={t('messages.creditsDepleted.chooseAPack')}>
                {displayPacks.map((pack, index) => {
                  const selected = selectedPack?.id === pack.id;
                  const price = priceFor(pack);
                  return <Pressable key={pack.id} ref={node => { packRefs.current[index] = node; }} onPress={() => selectPack(pack.id)} disabled={isBuying}
                    accessibilityRole="radio" accessibilityLabel={`${formatCreditBalance(pack.credits)} ${t('messages.creditsDepleted.credits')}, ${price || t('messages.creditsDepleted.priceUnavailable')}`}
                    accessibilityState={{ checked: selected, disabled: isBuying }}
                    {...(Platform.OS === 'web' ? { 'aria-checked': selected, tabIndex: selected ? 0 : -1, onKeyDown: event => onPackKeyDown(event, index) } : {})}
                    style={state => [styles.pack, selected && styles.selectedPack, quietPress(state)]}>
                    <Text size="l" weight="bold" style={styles.ink}>{formatCreditBalance(pack.credits)}</Text>
                    <Text size="xs" style={styles.body}>{t('messages.creditsDepleted.credits')}</Text>
                    <Text size="s" weight="medium" style={styles.packPrice}>{price || '—'}</Text>
                  </Pressable>;
                })}
              </View>
              <Pressable onPress={handleBuy} disabled={!canBuy} accessibilityRole="button" accessibilityState={{ disabled: !canBuy, busy: isBuying }}
                style={state => [styles.primary, !canBuy && styles.disabled, quietPress(state)]}>
                {isBuying && <ActivityIndicator size="small" color={dark ? '#202124' : '#FFFFFF'} />}
                <Text size="n" weight="bold" style={styles.primaryText}>{isBuying ? t('messages.creditsDepleted.openingCheckout') : selectedPrice
                  ? t('messages.creditsDepleted.addPack', { count: formatCreditBalance(selectedPack.credits), price: selectedPrice })
                  : t('messages.creditsDepleted.priceUnavailable')}</Text>
              </Pressable>
            </>}
        {!!purchaseError && <Text size="s" style={styles.body} accessibilityRole="alert" accessibilityLiveRegion="polite">{purchaseError}</Text>}
      </View>}
      {isPlus && <View style={[styles.secondary, { flexDirection: 'column', alignItems: 'stretch', gap: 8 }]}>
        <Text size="xs" style={styles.body}>{t(resetDate ? 'messages.creditsDepleted.refreshDate' : 'plusOutOfCredits.refreshOnRenewal', { total: formatCreditBalance(monthlyTotal), date: formatCreditResetDate(resetDate) })}</Text>
        <Pressable accessibilityRole="button" onPress={() => startAryaChat({ source: 'chat_inline' })} style={state => [styles.link, quietPress(state)]}>
          <Text size="s" weight="medium" style={styles.ink}>{t('messages.creditsDepleted.continueWithArya')}</Text>
        </Pressable>
        {canUpgradeToPremium && <Pressable accessibilityRole="button" onPress={handleUpgrade} style={state => [styles.link, quietPress(state)]}>
          <Text size="xs" style={styles.body}>{t('plusOutOfCredits.explorePremium')}</Text>
        </Pressable>}
      </View>}

      {isPlus && <View style={styles.footer}>
        <Pressable onPress={handleBuyCredits} accessibilityRole="button" style={state => [styles.link, quietPress(state)]}>
          <Text size="xs" style={styles.body}>{t('messages.creditsDepleted.seeAllOptions')}</Text>
        </Pressable>
        <Pressable onPress={() => openModal('CreditsInfoModal')} accessibilityRole="button" style={state => [styles.link, quietPress(state)]}>
          <Text size="xs" style={styles.body}>{t('messages.creditsDepleted.whatAreCredits')}</Text>
        </Pressable>
      </View>}
    </View>
  );
}

CreditsDepletedMessage.propTypes = { message: PropTypes.shape({ content: PropTypes.string }) };
CreditsDepletedMessage.displayName = 'CreditsDepletedMessage';
export default React.memo(CreditsDepletedMessage);
